import { Injectable } from '@angular/core';

import { BetslipConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Store, createSelector } from '@ngrx/store';
import { BehaviorSubject, Observable, map, of, switchMap } from 'rxjs';

import { BetslipType } from '../../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { getBetBuilderSlipId } from '../../../bet-generation/services/slip.utils';
import { BetBuilderPick, LinearBetBuilderPickId } from '../../../linear-bet-builder/models';
import { selectBetBuilderPickLegsFactory as selectBetBuilderPickLegsCountFactory } from '../../../linear-bet-builder/selectors';
import LinearBetslipMessagesService from '../../../linear/linear-betslip-messages.service';
import BetslipPickActions from '../../../picks/actions';
import BetslipPickUtilsService from '../../../picks/services/betslip-pick-utils.service';
import { RewardTokenContext } from '../../../reward-tokens/reward-tokens.model';
import { selectIsRewardTokensSelectorVisible } from '../../../reward-tokens/selectors';
import { InfoTrackingContext } from '../../../tracking/models';
import { EntainUiBetBuilderPickRemovalTracking } from '../../../tracking/tracking.models';
import { selectBetBuilderBetPickErrorsFactory } from '../../../validation/selectors';
import { isErrorComboPrevention } from '../../../validation/services/utils/betslip-errors-utils';
import BetslipComboBetActions from '../../actions';
import { LinearComboBetTracking } from '../../models';
import { ILinearComboBetContainerService, LinearComboBetContainerViewModel, SgpBadgeType } from './linear-combo-bet-container-service.interface';

export const selectBetBuilderContainerStateFactory = (pickId: LinearBetBuilderPickId, tokenContext: RewardTokenContext) =>
    createSelector(
        selectIsRewardTokensSelectorVisible(tokenContext),
        selectBetBuilderBetPickErrorsFactory(pickId),
        selectBetBuilderPickLegsCountFactory(pickId),
        (isRewardSelectorVisible, moduleErrors, legsCount) => ({
            isRewardSelectorVisible,
            moduleErrors,
            legsCount,
        }),
    );

@Injectable()
export class LinearBetBuilderComboContainerService implements ILinearComboBetContainerService {
    private pick?: BetBuilderPick;
    readonly viewModel$: Observable<LinearComboBetContainerViewModel | null>;
    private readonly pick$: BehaviorSubject<BetBuilderPick | null>;

    constructor(
        private store: Store,
        private linearMessagesService: LinearBetslipMessagesService,
        private betslipPickUtilsService: BetslipPickUtilsService,
        private siteCore: Sitecore,
        private epcotConfig: EpcotConfig,
        private betslipConfig: BetslipConfig,
    ) {
        this.pick$ = new BehaviorSubject<BetBuilderPick | null>(null);
        this.viewModel$ = this.createViewModel();
    }

    remove(): void {
        if (!this.pick) {
            return;
        }

        if (BetslipBetBuilderPick.isEntainUiBetBuilderPick(this.pick)) {
            //On click of clearAll of SGP in betslip
            this.betslipPickUtilsService.trackEntainUiBetBuilderPickRemoval(EntainUiBetBuilderPickRemovalTracking.TrackingAllPickRemovalSgp);
        } else {
            this.betslipPickUtilsService.trackNonSgpPickRemoval(EntainUiBetBuilderPickRemovalTracking.TrackingSinglePickRemoval);
        }

        this.store.dispatch(BetslipPickActions.requestRemovePick({ pickId: this.pick.id }));
    }

    updatePick(pick: BetBuilderPick | undefined): void {
        this.pick = pick;
        this.pick$.next(pick ?? null);
    }

    readonly isExpandedByDefault = false;

    toggleEachWay(): void {}

    toggleExpansion(isExpanded: boolean): void {
        this.store.dispatch(
            BetslipComboBetActions.linearPickListVisibilitySwitch({
                positionEvent: LinearComboBetTracking.SgpPositionEvent,
                eventDetails: isExpanded ? LinearComboBetTracking.ViewPickListEventDetails : LinearComboBetTracking.HidePickListEventDetails,
            }),
        );
    }

    private createViewModel(): Observable<LinearComboBetContainerViewModel | null> {
        return this.pick$.pipe(
            switchMap((pick) => {
                if (!pick) {
                    return of(null);
                }

                const isPickAreaExpandable = this.betslipConfig.linearModeCheckboxesEnabled;
                const tokenContext = { betslipType: BetslipType.BetBuilder, pickId: pick.id };
                const state$ = this.store.select(selectBetBuilderContainerStateFactory(pick.id, tokenContext));

                return state$.pipe(
                    map(({ isRewardSelectorVisible, moduleErrors, legsCount }) => {
                        return <LinearComboBetContainerViewModel>{
                            multiTitle: this.getMultiTitle(legsCount),
                            multiTitleBadge: this.betslipConfig.isSgpBadgeInTitleEnabled ? SgpBadgeType.Sgp : undefined,
                            subTitle: pick.eventName.name,
                            isPickAreaExpandable,
                            errors: moduleErrors,
                            hasComboPrevention: moduleErrors.some(isErrorComboPrevention),
                            isErrorVisible: moduleErrors.length > 0,
                            isEpcotEnabled: this.epcotConfig.isEnabled,
                            isEachWay: false,
                            showEachWayToggle: false,
                            placeablePicksCount: 1,
                            totalPicksCount: 1,
                            tokenContext,
                            isRewardSelectorVisible,
                            betslipType: BetslipType.BetBuilder,
                            slipId: getBetBuilderSlipId(pick.id),
                            showRemoveButton: true,
                            showInfoIcon: this.epcotConfig.isEnabled,
                            showDetailsSection: !isPickAreaExpandable,
                            showSelectionCounter: false,
                            infoMessage: this.siteCore.betslip.messages.BetbuilderComboInfoText,
                            infoHeaderTitle: this.siteCore.betslip.messages.BetbuilderComboHeaderTitle,
                            infoHeader: this.siteCore.betslip.messages.BetbuilderComboInfoHeader,
                            infoTrackingContext: InfoTrackingContext.Sgp,
                            isTotalOddsIndicatorEnabled: true,
                            showTokenSummaryUnderTitle: false,
                            showBetbuilderLiveIndicator: pick.isLive,
                        };
                    }),
                );
            }),
        );
    }

    private getMultiTitle(selectedPicksCount: number): string {
        return this.linearMessagesService.getBetBuilderComboSectionTitle(selectedPicksCount);
    }
}
